// Popups explicativos en castellano. La version en ingles esta en
// explicaciones.en.js y hud/explicaciones.js elige segun el idioma. Cada
// entrada tiene un codigo (linea superior estilo HUD), un titulo y el cuerpo
// en JSX. El texto se sostiene solo: describe el concepto sin referirse a la
// conversacion que lo origino.

// Primera pagina del briefing: el concepto, igual en las dos versiones.
const INTRO_CONCEPTO = (
  <>
    <p>
      La accesibilidad de segundo orden mide el acceso a servicios cotidianos
      considerando también el lugar de trabajo, además de la vivienda. El
      marco de la ciudad de 15 minutos suele medirlo solo desde la casa, y las
      personas pasan gran parte del día trabajando.
    </p>
    <p>
      El mapa cubre el Gran Santiago, en Chile. Cada hexágono es una celda de
      la ciudad, coloreada según su accesibilidad a farmacias, supermercados,
      bancos y centros de salud caminando.
    </p>
  </>
);

// Segunda pagina: los controles. El layout movil no trae leyenda, selector de
// alfa, botones ? ni tooltip al pasar el cursor, asi que lleva su propia
// version y App.js elige segun el ancho.
const INTRO_CONTROLES = (
  <>
    <p>
      Haz clic en una celda para lanzar un escaneo. El pulso{' '}
      <strong>dorado</strong> recorre lo que sus residentes alcanzan a pie en
      30 minutos; los ecos <span className="modal-rojo">rojos</span> hacen lo
      mismo desde donde trabajan.
    </p>
    <p>
      Los botones de <strong>α</strong> del panel de color suman a los
      trabajadores a la demanda. Los signos <strong>?</strong> abren la
      explicación de cada panel.
    </p>
    <p>
      Al pasar el cursor sobre una celda se muestra su resumen sin escanear.
      También puedes navegar con las flechas del teclado: cada pulsación mueve
      la selección a la celda vecina y relanza el escaneo.
    </p>
  </>
);

const INTRO_CONTROLES_MOVIL = (
  <>
    <p>
      Toca una celda para lanzar un escaneo. El pulso <strong>dorado</strong>{' '}
      recorre lo que sus residentes alcanzan a pie en 30 minutos; los ecos{' '}
      <span className="modal-rojo">rojos</span> hacen lo mismo desde donde
      trabajan.
    </p>
    <p>
      El color de los hexágonos mide la accesibilidad con α = 0,5: cada
      trabajador cuenta como media persona en la demanda del lugar donde
      trabaja.
    </p>
    <p>
      La franja inferior resume la celda elegida: su puntuación, su tipo y los
      servicios que lleva encendidos. El recuadro LOICA de la esquina abre los
      créditos y el método.
    </p>
  </>
);

export const EXPLICACIONES = {
  intro: {
    codigo: 'BRIEFING // 00',
    titulo: 'Accesibilidad de segundo orden',
    paginas: [INTRO_CONCEPTO, INTRO_CONTROLES],
    paginasMovil: [INTRO_CONCEPTO, INTRO_CONTROLES_MOVIL],
  },

  e2sfca: {
    codigo: 'INDICE // E2SFCA',
    titulo: 'Puntuación E2SFCA',
    cuerpo: (
      <>
        <p>
          El índice E2SFCA (enhanced two-step floating catchment area) resume en
          un número entre 0 y 1 qué tan bien servida está una celda. Pondera la
          oferta de servicios contra la demanda de personas que compiten por
          ellos.
        </p>
        <p>
          El cálculo avanza en dos pasos. Primero, cada servicio reparte su
          capacidad entre toda la población que lo alcanza a pie. Después, cada
          celda suma las porciones de los servicios a su alcance.
        </p>
        <div className="modal-formula">
          alcance según el tiempo de viaje: menos de 10 min pesa 1,00; de 10 a 20
          min pesa 0,42; de 20 a 30 min pesa 0,03
        </div>
        <p>
          El alcance decae con el tiempo de viaje, así que los servicios
          cercanos brillan más que los lejanos en la animación. Las barras
          desglosan el índice por tipo; el total los combina, con farmacias y
          supermercados al doble del peso de bancos y salud.
        </p>
      </>
    ),
  },

  alpha: {
    codigo: 'PARAMETRO // α',
    titulo: 'El parámetro α',
    cuerpo: (
      <>
        <p>
          α controla cuánto pesan los trabajadores en la demanda de cada celda.
        </p>
        <div className="modal-formula">
          demanda = residentes + α por trabajadores
        </div>
        <p>
          Con <strong>α = 0</strong> solo cuentan los residentes: es la
          accesibilidad residencial habitual, el panel A de la figura. Con{' '}
          <strong>α = 1</strong> cada trabajador compite tanto como un residente
          por los mismos servicios.
        </p>
        <p>
          Al subir α, la demanda crece donde se concentra el empleo y la
          puntuación baja. En el Gran Santiago la media cae de 0,35 con α = 0 a
          0,27 con α = 1. Esa caída distingue a los centros de los polos de
          trabajo.
        </p>
      </>
    ),
  },

  segundoOrden: {
    codigo: 'CONCEPTO // 2°',
    titulo: 'Segundo orden',
    cuerpo: (
      <>
        <p>
          La accesibilidad de <strong>primer orden</strong> mide lo que una
          persona alcanza desde su casa. La de{' '}
          <span className="modal-rojo">segundo orden</span> mide lo que alcanza
          desde su trabajo.
        </p>
        <p>
          Al hacer clic en una celda, un modelo de asignación casa-trabajo
          estima dónde trabajan sus residentes ocupados. Desde cada uno de esos
          destinos se buscan de nuevo los servicios a pie.
        </p>
        <p>
          El panel reporta cuántos de esos servicios son{' '}
          <span className="modal-rojo">nuevos</span>: los que un residente
          alcanza desde el trabajo pero no desde la casa. Ese conjunto es el que
          la accesibilidad residencial deja fuera.
        </p>
      </>
    ),
  },

  tipologia: {
    codigo: 'PANEL A // TIPOLOGIA',
    titulo: 'Tipología de la celda',
    cuerpo: (
      <>
        <p>
          El panel principal de la figura cruza dos cantidades: la accesibilidad
          que la celda <strong>retiene</strong> cuando los trabajadores entran
          en la demanda (α = 0,5) y el <strong>peso</strong> de la población
          trabajadora, la fracción de la población diurna de la celda que llega
          a trabajar.
        </p>
        <p>De ese cruce salen cuatro esquinas:</p>
        <ul>
          <li>
            <strong>Centro de uso mixto</strong>: llegan trabajadores y los
            servicios aguantan (centro histórico, eje de oficinas del
            nororiente, centros de Maipú y Puente Alto).
          </li>
          <li>
            <strong>Distrito monofuncional de empleo</strong>: llegan
            trabajadores y no hay servicios (Quilicura, Cerrillos, Maipú
            industrial, Ciudad Empresarial).
          </li>
          <li>
            <strong>Zona residencial bien servida</strong>: pocos trabajadores
            y buen acceso a servicios.
          </li>
          <li>
            <strong>Zona desatendida</strong>: pocos trabajadores y poco acceso
            a servicios.
          </li>
        </ul>
        <p>
          La matriz resalta en cuál cae la celda elegida. El botón{' '}
          <strong>tipología</strong> del panel de color pinta el mapa completo
          con esos nueve colores.
        </p>
      </>
    ),
  },

  escaneo: {
    codigo: 'ESCANEO // 30 MIN',
    titulo: 'El escaneo',
    cuerpo: (
      <>
        <p>
          El escaneo anima el área de captación de la celda: en unos segundos
          recorre los servicios que sus residentes alcanzan caminando, hasta un
          máximo de 30 minutos.
        </p>
        <p>
          Los tiempos de viaje son reales, calculados con el motor R5 sobre la
          red de calles de OpenStreetMap. Por eso el frente no avanza en
          círculos perfectos: sigue la forma de la trama vial.
        </p>
        <p>
          El pulso <strong>dorado</strong> es el primer orden, desde la celda
          elegida. Los ecos <span className="modal-rojo">rojos</span> son el
          segundo orden, desde los lugares de trabajo. Los puntos marcan cada
          servicio alcanzado, con color según su tipo.
        </p>
      </>
    ),
  },
};
